'use server'

import { apiRequest, ApiResponse } from '../request'

export type LetterheadMode = 'BANDS' | 'FULL_PAGE' | 'RESERVE'
export type LetterheadPageSize = 'A4' | 'A5'
export type LetterheadWatermarkType = 'NONE' | 'TEXT' | 'IMAGE'

/** Posição da imagem dentro do timbrado. */
export type LetterheadImageSlot =
    | 'header'
    | 'footer'
    | 'background'
    | 'watermark'

export interface Letterhead {
    id: number
    companyId: number
    name: string
    description?: string | null
    active: boolean
    isDefault: boolean
    mode: LetterheadMode
    pageSize: LetterheadPageSize
    documentTypes: string[]

    headerImageUrl?: string | null
    headerImageWidth?: number | null
    headerImageHeight?: number | null

    footerImageUrl?: string | null
    footerImageWidth?: number | null
    footerImageHeight?: number | null

    backgroundImageUrl?: string | null
    backgroundImageWidth?: number | null
    backgroundImageHeight?: number | null
    backgroundFirstPageOnly: boolean

    marginTopMm: number
    marginBottomMm: number
    marginLeftMm: number
    marginRightMm: number

    watermarkType: LetterheadWatermarkType
    watermarkText?: string | null
    watermarkImageUrl?: string | null
    watermarkImageWidth?: number | null
    watermarkImageHeight?: number | null
    watermarkOpacity: number
    watermarkRotation: number

    hideDefaultHeader: boolean
    hideDefaultFooter: boolean

    createdAt: string
    updatedAt: string
}

export type LetterheadPayload = Partial<
    Pick<
        Letterhead,
        | 'name'
        | 'description'
        | 'active'
        | 'isDefault'
        | 'mode'
        | 'pageSize'
        | 'documentTypes'
        | 'backgroundFirstPageOnly'
        | 'marginTopMm'
        | 'marginBottomMm'
        | 'marginLeftMm'
        | 'marginRightMm'
        | 'watermarkType'
        | 'watermarkText'
        | 'watermarkOpacity'
        | 'watermarkRotation'
        | 'hideDefaultHeader'
        | 'hideDefaultFooter'
    >
>

export async function getLetterheads(): Promise<ApiResponse<Letterhead[]>> {
    return apiRequest<Letterhead[]>('letterhead', {})
}

export async function getLetterhead(
    id: number,
): Promise<ApiResponse<Letterhead>> {
    return apiRequest<Letterhead>(`letterhead/${id}`, {})
}

export async function createLetterhead(
    data: LetterheadPayload,
): Promise<ApiResponse<Letterhead>> {
    return apiRequest<Letterhead>('letterhead', { data }, 'post')
}

export async function updateLetterhead(
    id: number,
    data: LetterheadPayload,
): Promise<ApiResponse<Letterhead>> {
    return apiRequest<Letterhead>(`letterhead/${id}`, { data }, 'patch')
}

export async function setDefaultLetterhead(
    id: number,
): Promise<ApiResponse<Letterhead>> {
    return apiRequest<Letterhead>(`letterhead/${id}/default`, {}, 'patch')
}

export async function deleteLetterhead(
    id: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`letterhead/${id}`, {}, 'delete')
}

export async function uploadLetterheadImage(
    id: number,
    slot: LetterheadImageSlot,
    file: File,
): Promise<ApiResponse<Letterhead>> {
    const formData = new FormData()
    formData.append('image', file)
    return apiRequest<Letterhead>(
        `letterhead/${id}/image/${slot}`,
        { data: formData, formData: true },
        'post',
    )
}

export async function removeLetterheadImage(
    id: number,
    slot: LetterheadImageSlot,
): Promise<ApiResponse<Letterhead>> {
    return apiRequest<Letterhead>(`letterhead/${id}/image/${slot}`, {}, 'delete')
}
